import React from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  DEFAULT_NEXT_ACTION,
  TOTAL_STEPS,
  WORKFLOW_STEPS,
} from "../constants/workflowSteps";

function Row({ label, value, valueColor }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        fontSize: 13,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: valueColor || "text.primary",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * `workflowStep` is the same index that drives the header progress bar, so the
 * panel always reports the step the header highlights. `busy` means work is in
 * flight on that step; `nextAction` overrides the step's default owed action.
 */
export default function ActivityCard({
  workflowStep = 0,
  nextAction,
  busy = false,
}) {
  const theme = useTheme();
  const step = Math.min(Math.max(workflowStep, 0), TOTAL_STEPS);
  const allComplete = step >= TOTAL_STEPS;

  const status = allComplete
    ? "All Steps Complete"
    : busy
      ? "Working…"
      : `Step ${step + 1} In Progress`;
  const currentStep = allComplete
    ? WORKFLOW_STEPS[TOTAL_STEPS - 1]
    : WORKFLOW_STEPS[step];
  const owedAction = allComplete
    ? "Nothing — workflow finished"
    : nextAction || DEFAULT_NEXT_ACTION[step];

  return (
    <Box
      sx={{
        borderRadius: 1,
        p: 3,
        bgcolor: "background.paper",
        boxShadow: "0 18px 52px rgba(15, 23, 42, 0.06)",
        minWidth: 280,
        display: {
          xs: "none",
          sm: "none",
          md: "block",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle1" fontWeight={700}>
          OnePass Activity
        </Typography>
        {busy ? <CircularProgress size={20} color="primary" /> : null}
      </Box>

      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <Row
          label="Status"
          value={status}
          valueColor={
            allComplete ? "success.main" : busy ? "primary.main" : "text.primary"
          }
        />
        <Row label="Current Step" value={currentStep} />
        <Row
          label="Next Action"
          value={owedAction}
          // The outstanding action is the one thing the user must act on, so it
          // stays visually distinct until the workflow is finished.
          valueColor={allComplete ? "text.secondary" : "warning.dark"}
        />
      </Stack>

      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
        <Stack spacing={1.25}>
          {WORKFLOW_STEPS.map((label, i) => {
            const done = i < step;
            const active = !done && i === step;
            // Steps past the current one are not reachable yet and say so,
            // matching the padlock the header shows on the next step.
            const locked = i > step;
            return (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: done
                    ? "success.main"
                    : active
                      ? "primary.main"
                      : "text.secondary",
                  fontSize: 12,
                }}
              >
                {done ? (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 16, color: theme.palette.success.main }}
                  />
                ) : active && busy ? (
                  <CircularProgress size={16} thickness={5} color="primary" />
                ) : locked ? (
                  <LockOutlinedIcon sx={{ fontSize: 14 }} />
                ) : (
                  <FiberManualRecordIcon sx={{ fontSize: 10 }} />
                )}
                <Typography sx={{ fontWeight: active ? 700 : 500 }}>
                  {`${i + 1}. ${label}`}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
        <Box sx={{ width: "100%", borderRadius: 999, bgcolor: "divider", height: 10, overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              width: `${(step / TOTAL_STEPS) * 100}%`,
              bgcolor: "primary.main",
              transition: "width 0.7s ease",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
